import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getShipmentSummary, getClearingSummary, getContractSummary } from '@/services/api/ai'

export type AIEntityType = 'shipment' | 'clearing' | 'contract'

interface AISummaryModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: AIEntityType
  entityId: string | null
}

const AISummaryModal = ({ isOpen, onClose, entityType, entityId }: AISummaryModalProps) => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !entityId) {
      setSummary('')
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    const fetchSummary = async () => {
      try {
        const text =
          entityType === 'shipment'
            ? await getShipmentSummary(entityId)
            : entityType === 'clearing'
              ? await getClearingSummary(entityId)
              : await getContractSummary(entityId)
        if (!cancelled) {
          setSummary(text)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
              : err instanceof Error
                ? err.message
                : 'Failed to load AI summary.'
          setError(message ?? 'Failed to load AI summary.')
          setSummary('')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [isOpen, entityType, entityId])

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      onClick={onClose}
      style={{ zIndex: 80 }}
    >
      <div
        className="bg-white shadow-xl rounded-lg flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '480px',
          maxWidth: '95vw',
          zIndex: 81,
          fontFamily: 'Lexend Deca, sans-serif',
        }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-semibold text-gray-900">AI Summary</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-200 text-gray-600"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-4 py-4 overflow-y-auto flex-1 min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <svg
                className="animate-spin h-10 w-10 text-blue-600 mb-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Generating summary…</span>
            </div>
          )}

          {!loading && error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          {!loading && !error && summary && (
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-gray-800 text-sm leading-relaxed">{summary}</div>
            </div>
          )}

          {!loading && !error && !summary && entityId && (
            <p className="text-gray-500 text-sm">No summary generated.</p>
          )}
        </div>

        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500">
            This is an AI-generated assistant response. Please verify critical decisions.
          </p>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AISummaryModal
